<script setup lang="ts">
import {computed} from 'vue';
import FlowSignMark from './FlowSignMark.vue';

// Define props interface
interface Props {
  image?: string;
  tint?: 'electric-yellow' | 'red' | 'cyan' | 'magenta' | 'none';
  knockedOut?: boolean;
  /** Character is Signed with Flow — under game master authority. */
  signed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  image: undefined,
  tint: 'none',
  knockedOut: false,
  signed: false
});

// Placeholder image - a simple data URI for a gray circle
const placeholderImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="40" fill="%23cccccc"/%3E%3Ctext x="50" y="55" font-size="14" text-anchor="middle" fill="%23666666"%3ENo Image%3C/text%3E%3C/svg%3E';

// Use provided image or fallback to placeholder
const characterImage = computed(() => props.image || placeholderImage);

// Compute tint class
const tintClass = computed(() => props.tint !== 'none' ? `tint-${props.tint}` : '');
</script>

<template>
  <div class="character-token token-background">
    <!-- Background layer -->
    <div class="character-image-container" :class="{ 'knocked-out': knockedOut }" :style="{ backgroundImage: `url(${characterImage})` }"></div>
    <div v-if="knockedOut" class="death-overlay"></div>
    <div class="token-overlay-1" :class="tintClass"></div>
    <div class="token-overlay-2" :class="tintClass"></div>
    <FlowSignMark v-if="signed" :size="16"/>

    <!-- Death overlay - shown when knocked out -->

  </div>
</template>

<style scoped>
.character-token {
  position: relative;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Background layer */
.token-background {
  z-index: 1;
  background-image: url('@/assets/rpg_token_2/token.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.character-image-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 85%;
  height: 85%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 50%;
  mask-image: radial-gradient(circle, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 0.2) 95%);
}

.token-overlay-1 {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-image: url('@/assets/rpg_token_2/token-overlay.png');
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  mix-blend-mode: screen;
  opacity: 0.5;
}
.token-overlay-2 {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-image: url('@/assets/rpg_token_2/token-glow.png');
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  opacity: 0.4;
}

/* Tint classes - rotate from cyan (base color) */
.tint-electric-yellow {
  filter: hue-rotate(-120deg);
}

.tint-red {
  filter: hue-rotate(-205deg);
}

.tint-cyan {
  filter: hue-rotate(0deg);
}

.tint-magenta {
  filter: hue-rotate(120deg);
}

/* Knocked out state - grayscale filter and reduced opacity */
.character-image-container.knocked-out {
  filter: grayscale(100%);
  opacity: 0.5;
}

/* Death overlay - skull image */
.death-overlay {
  position: absolute;
  background: url('@/assets/images/avatar/death.webp') no-repeat center center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50%;
  height: 50%;
  background-size: contain;
  border-radius: 50%;
  mask-image: radial-gradient(circle, rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 0.2) 90%);
  opacity: 0.8;
}

</style>